import { localUrl as appLocalUrl, assetUrl as appAssetUrl } from '../app-path.js';
// Transport is intentionally scoped to home weather; auth/favorites keep their clients.
export const WEATHER_TIMEOUT_MS = 24000;

// Each active load owns its terminal events; collectors keep every original push.
const terminalEvents = new WeakMap();
export const reportWeatherUnavailable = (component, context) => {
  if (!context || context.signal?.aborted || !context.isCurrent()) return false;
  const seen = terminalEvents.get(context) || new Set();
  if (seen.has(component)) return false;
  seen.add(component);
  terminalEvents.set(context, seen);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'error_hit', name: `data_aggregator_home_${component}_unavailable` });
  return true;
};

export const parseWeatherJSON = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    const starts = [text.indexOf('{'), text.indexOf('[')].filter(index => index >= 0);
    const start = Math.min(...starts);
    const end = Math.max(text.lastIndexOf('}'), text.lastIndexOf(']'));
    if (!starts.length || end <= start) throw new Error('Resposta não contém JSON válido.');
    return JSON.parse(text.slice(start, end + 1));
  }
};

export async function fetchWeatherJSON(url, options = {}) {
  const { signal, ...requestOptions } = options;
  const controller = new AbortController();
  const abortError = () => Object.assign(new Error('Requisição de tempo cancelada.'), { name: 'AbortError' });
  let timer;
  let cancel;
  const cancelled = new Promise((_, reject) => {
    cancel = () => {
      controller.abort();
      reject(abortError());
    };
  });
  if (signal?.aborted) cancel();
  else signal?.addEventListener('abort', cancel, { once: true });
  timer = setTimeout(cancel, WEATHER_TIMEOUT_MS);

  try {
    // Race the body as well: cancellation must settle even if a transport ignores abort.
    const request = (async () => {
      if (controller.signal.aborted) throw abortError();
      const response = await fetch(url, { ...requestOptions, signal: controller.signal });
      if (!response.ok) throw new Error(`Erro HTTP: ${response.status}`);
      return parseWeatherJSON(await response.text());
    })();
    return await Promise.race([request, cancelled]);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', cancel);
  }
}

export async function geolocationPermission(signal) {
  let timer;
  let cancel;
  try {
    const fallback = new Promise(resolve => {
      cancel = () => resolve('prompt');
      timer = setTimeout(cancel, WEATHER_TIMEOUT_MS);
      if (signal?.aborted) cancel();
      else signal?.addEventListener('abort', cancel, { once: true });
    });
    const permission = Promise.resolve(navigator.permissions?.query({ name: 'geolocation' }))
      .then(result => result?.state || 'prompt');
    return await Promise.race([permission, fallback]);
  } catch {
    return 'prompt';
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', cancel);
  }
}

export const weatherNumber = value => (
  (typeof value === 'number' || (typeof value === 'string' && value.trim() !== ''))
  && Number.isFinite(Number(value)) ? Number(value) : null
);

export const weatherText = value => (
  typeof value === 'string' && value.trim()
    && !['-', '--', 'sem dados', 'undefined', 'null'].includes(value.trim().toLowerCase()) ? value.trim() : ''
);

export const weatherIcon = value => (
  weatherText(value) && /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(value)
    && !['null', 'undefined'].includes(value) ? value : ''
);

export const weatherQualityState = (result, usable, complete = usable) => {
  if (!usable || result?.error || result?.success === false) return 'unavailable';
  const state = result?.quality?.state;
  if (state && !['success', 'partial', 'stale'].includes(state)) return 'unavailable';
  if (state === 'stale' || /stale/i.test(result?.quality?.source || '')) return 'stale';
  if (state === 'partial' || result?.quality?.cacheable === false || result?.cacheable === false) return 'partial';
  return complete ? 'success' : 'partial';
};

export const weatherCityUrls = locale => {
  const city = weatherText(locale?.city);
  const uf = weatherText(locale?.ac === 'BR' || !locale?.ac ? locale?.uf : locale?.ac);
  if (!city || !uf || !locale?.idcity) return null;
  const slug = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const cityUrl = `${locale.idcity}/${slug}-${uf.toLowerCase()}`;
  return {
    cityName: `${city}, ${uf}`, cityUrl,
    todayUrl: appLocalUrl(`/previsao-do-tempo/cidade/${cityUrl}`),
    tomorrowUrl: appLocalUrl(`/previsao-do-tempo/amanha/cidade/${cityUrl}`),
    city15DaysUrl: appLocalUrl(`/previsao-do-tempo/15-dias/cidade/${cityUrl}`)
  };
};

export const setWeatherImage = ($image, icon, alt = '', outline = false) => {
  if (weatherIcon(icon)) {
    const src = appAssetUrl(`/dist/images/v2/svg/${outline ? 'outline/' : ''}${icon}.svg`);
    $image.attr({ src, 'data-src': src, alt }).css('visibility', '').removeAttr('data-weather-unavailable');
  } else {
    $image.removeAttr('data-src').attr({ src: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==', alt: 'Ícone de previsão indisponível', 'data-weather-unavailable': 'home-icon' }).css('visibility', 'hidden');
  }
};
